import React, { useState, useCallback, useEffect } from 'react';
import List from './List';
import AddWorkoutModal from './AddWorkoutModal';
import AddMealModal from './AddMealModal';
import SettingsModal from './SettingsModal';
import StickyFooter from './StickyFooter';
import DebugOverlay from './DebugOverlay';
import WeekNavigationBar from './WeekNavigationBar';
import { StorageManager } from '../services/storage/StorageManager';
import LoadingSpinner from './Spinner';
import { DataItem, WorkoutData, MealData, AppSettings, ProfileData } from '../types';
import { StorageProvider } from '../services/storage/types';

const App: React.FC = () => {
    const [dataVersion, setDataVersion] = useState(0);
    const [loadedItemsCount, setLoadedItemsCount] = useState(0); // New state
    const [isStorageReady, setIsStorageReady] = useState(false);
    const [storageError, setStorageError] = useState<string | null>(null);
    const [isWorkoutModalOpen, setIsWorkoutModalOpen] = useState(false);
    const [isMealModalOpen, setIsMealModalOpen] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [settings, setSettings] = useState<AppSettings>({
        showStorageControls: false,
        showDebugOverlay: true,
        debugOverlayMinimized: false
    });
    const [profileId, setProfileId] = useState<number | null>(null);
    const [windowHeight, setWindowHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 800);
    const [storageProvider, setStorageProvider] = useState<StorageProvider | null>(null);
    const [editingItem, setEditingItem] = useState<DataItem | null>(null);
    const [scrollToTimestamp, setScrollToTimestamp] = useState<number | undefined>(undefined);
    const [currentWeek, setCurrentWeek] = useState<string | undefined>(undefined);

    const handleDataSeeded = () => {
        setDataVersion(prevVersion => prevVersion + 1);
        // Reset count on new seed, InfiniteList will update it
        setLoadedItemsCount(0);
    };

    const handleDataCleared = () => {
        setDataVersion(prevVersion => prevVersion + 1);
        // Reset count when data is cleared, InfiniteList will update it
        setLoadedItemsCount(0);
    };

    const handleItemsCountChange = useCallback((count: number) => {
        setLoadedItemsCount(count);
    }, []);

    // Initialize storage on app startup
    useEffect(() => {
        const initializeStorage = async () => {
            try {
                if (!StorageManager) {
                    throw new Error('StorageManager is not defined - module loading failed');
                }
                if (typeof StorageManager.getProvider !== 'function') {
                    throw new Error('StorageManager.getProvider is not a function - module export failed');
                }
                
                const provider = await StorageManager.getProvider();
                setStorageProvider(provider);
                setIsStorageReady(true);
                
                // Load or create user profile first
                await loadOrCreateProfile();
                
                // Check if data exists, if not seed 90 days of data
                const count = await provider.getTotalItemCount();
                if (count === 0) {
                    console.log('No data found, seeding 180 days of workout and meal data...');
                    await provider.seedData(500, 180); // This will trigger 180-day seeding
                    setDataVersion(prevVersion => prevVersion + 1);
                }
            } catch (error) {
                console.error('Failed to initialize storage:', error);
                setStorageError(error instanceof Error ? error.message : 'Storage initialization failed');
            }
        };

        initializeStorage();
    }, []);

    // Handle window resize for dynamic height calculation
    useEffect(() => {
        const handleResize = () => {
            setWindowHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleProviderChange = async () => {
        // Reset data when switching providers
        setIsStorageReady(false);
        setStorageError(null);
        setDataVersion(prevVersion => prevVersion + 1);
        setLoadedItemsCount(0);

        try {
            await StorageManager.getProvider();
            setIsStorageReady(true);
            console.log("storage is ready")
        } catch (error) {
            console.error('Failed to switch storage provider:', error);
            setStorageError(error instanceof Error ? error.message : 'Storage provider switch failed');
        }
    };

    const handleSaveWorkout = async (workoutData: { name: string; description: string; imageUrl: string; category: 'workout'; timestamp: number; data: WorkoutData }) => {
        try {
            const provider = await StorageManager.getProvider();
            
            if (editingItem && editingItem.category === 'workout') {
                // Update existing item - use new timestamp from modal
                await provider.updateItem(editingItem.id, workoutData);
                console.log('Updated workout:', editingItem.id);
            } else {
                // Add new item with current timestamp
                await provider.addItem(workoutData);
                console.log('Added new workout');
            }
            
            setDataVersion(prevVersion => prevVersion + 1);
            setEditingItem(null);
        } catch (error) {
            console.error('Failed to save workout:', error);
        }
    };

    const handleSaveMeal = async (mealData: { name: string; description: string; imageUrl: string; category: 'meal'; timestamp: number; data: MealData }) => {
        try {
            const provider = await StorageManager.getProvider();
            
            if (editingItem && editingItem.category === 'meal') {
                // Update existing item - use new timestamp from modal
                await provider.updateItem(editingItem.id, mealData);
                console.log('Updated meal:', editingItem.id);
            } else {
                // Add new item with current timestamp
                await provider.addItem(mealData);
                console.log('Added new meal');
            }
            
            setDataVersion(prevVersion => prevVersion + 1);
            setEditingItem(null);
        } catch (error) {
            console.error('Failed to save meal:', error);
        }
    };

    const handleSettingsChange = async (newSettings: AppSettings) => {
        setSettings(newSettings);
        
        // Update the profile with new settings
        if (profileId) {
            try {
                const provider = await StorageManager.getProvider();
                
                // Get the current profile
                const profiles = await provider.getItems(1, 10, { filterBy: 'profile' });
                if (profiles.length > 0) {
                    const currentProfile = profiles[0];
                    const profileData = currentProfile.data as ProfileData;
                    
                    // Update the profile with new settings
                    const updatedProfileData: ProfileData = {
                        ...profileData,
                        settings: newSettings
                    };
                    
                    await provider.updateItem(profileId, {
                        name: currentProfile.name,
                        description: currentProfile.description,
                        imageUrl: currentProfile.imageUrl,
                        category: currentProfile.category,
                        timestamp: currentProfile.timestamp,
                        data: updatedProfileData
                    });
                    
                    console.log('Settings saved to profile:', newSettings);
                }
            } catch (error) {
                console.error('Failed to save settings:', error);
            }
        }
    };

    const handleDebugOverlayMinimizeToggle = async (minimized: boolean) => {
        const newSettings = { ...settings, debugOverlayMinimized: minimized };
        await handleSettingsChange(newSettings);
    };

    const handleEditItem = (item: DataItem) => {
        console.log('App: Edit item requested:', item);
        setEditingItem(item);
        
        // Open the appropriate modal based on item category
        if (item.category === 'workout') {
            setIsWorkoutModalOpen(true);
        } else if (item.category === 'meal') {
            setIsMealModalOpen(true);
        } else if (item.category === 'profile') {
            // For profiles, we could open the settings modal or show a message
            alert('Profile editing is handled through the Settings menu.');
        }
    };

    const loadOrCreateProfile = async () => {
        try {
            const provider = await StorageManager.getProvider();
            
            // Look for existing profile
            const profiles = await provider.getItems(1, 10, { filterBy: 'profile' });
            
            if (profiles.length > 0) {
                // Load existing profile
                const profile = profiles[0];
                setProfileId(profile.id);
                
                if (profile.data && typeof profile.data === 'object') {
                    const profileData = profile.data as ProfileData;
                    
                    // Check if profile has settings, if not add default settings
                    if ('settings' in profileData && profileData.settings) {
                        setSettings(profileData.settings);
                    } else {
                        // Update existing profile to include default settings
                        const defaultSettings: AppSettings = {
                            showStorageControls: false,
                            showDebugOverlay: true,
                            debugOverlayMinimized: false
                        };
                        
                        const updatedProfileData: ProfileData = {
                            ...profileData,
                            settings: defaultSettings
                        };
                        
                        await provider.updateItem(profile.id, {
                            name: profile.name,
                            description: profile.description,
                            imageUrl: profile.imageUrl,
                            category: profile.category,
                            timestamp: profile.timestamp,
                            data: updatedProfileData
                        });
                        
                        setSettings(defaultSettings);
                    }
                }
            } else {
                // Create default profile
                const defaultProfile: Omit<DataItem, 'id'> = {
                    name: 'Default Profile',
                    description: 'Default user profile with app settings',
                    imageUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=300&fit=crop',
                    category: 'profile',
                    timestamp: Date.now(),
                    data: {
                        age: 30,
                        height: 70,
                        weight: 180,
                        activityLevel: 'moderate',
                        goals: ['Stay healthy', 'Build strength'],
                        restrictions: [],
                        targetCalories: 2200,
                        targetProtein: 150,
                        settings: {
                            showStorageControls: false,
                            showDebugOverlay: true,
                            debugOverlayMinimized: false
                        }
                    } as ProfileData
                };
                
                const newProfile = await provider.addItem(defaultProfile);
                setProfileId(newProfile.id);
                setSettings(defaultProfile.data.settings);
            }
        } catch (error) {
            console.error('Failed to load/create profile:', error);
        }
    };

    const handleWeekClick = useCallback((timestamp: number) => {
        setScrollToTimestamp(timestamp);
        // Reset after a short delay to allow for future clicks
        setTimeout(() => setScrollToTimestamp(undefined), 100);
    }, []);

    const handleCurrentWeekChange = useCallback((weekLabel: string) => {
        setCurrentWeek(weekLabel);
    }, []);

    if (storageError) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-gray-100 font-sans flex items-center justify-center">
                <div className="text-center p-8 text-red-400 bg-slate-700 rounded-lg max-w-md">
                    <h2 className="text-xl font-semibold mb-2">Storage Error</h2>
                    <p>{storageError}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!isStorageReady) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-gray-100 font-sans flex items-center justify-center">
                <LoadingSpinner message="Initializing storage..." />
            </div>
        );
    }

    return (
        <div className="h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-gray-100 font-sans flex flex-col overflow-hidden">
            {/* Week Navigation Bar - Sticky at top */}
            <WeekNavigationBar 
                onWeekClick={handleWeekClick}
                currentWeek={currentWeek}
            />
            
            {/* Main Content - Full height minus footer and nav bar */}
            <div className="flex-1 flex flex-col p-2 overflow-hidden" style={{ paddingBottom: '68px' }}>
                <main className="max-w-4xl mx-auto w-full h-full flex flex-col overflow-hidden">
                    {/* Data Controls */}

                    {/* List Container - Takes remaining height */}
                    <div className="bg-slate-800 shadow-2xl rounded-xl p-2 flex-1 overflow-hidden">
                        <div className="h-full">
                            <List 
                                key={dataVersion} 
                                onItemsCountChange={handleItemsCountChange} 
                                onEditItem={handleEditItem}
                                scrollToTimestamp={scrollToTimestamp}
                                onCurrentWeekChange={handleCurrentWeekChange}
                            />
                        </div>
                    </div>
                </main>
            </div>

            {/* Sticky Footer */}
            <StickyFooter
                onOpenWorkoutModal={() => setIsWorkoutModalOpen(true)}
                onOpenMealModal={() => setIsMealModalOpen(true)}
                onOpenSettings={() => setIsSettingsModalOpen(true)}
                onDataSeeded={handleDataSeeded}
            />
                
            {/* Modals */}
            <AddWorkoutModal
                isOpen={isWorkoutModalOpen}
                onClose={() => {
                    setIsWorkoutModalOpen(false);
                    setEditingItem(null);
                }}
                onSave={handleSaveWorkout}
                editingItem={editingItem}
            />
            <AddMealModal
                isOpen={isMealModalOpen}
                onClose={() => {
                    setIsMealModalOpen(false);
                    setEditingItem(null);
                }}
                onSave={handleSaveMeal}
                editingItem={editingItem}
            />
            <SettingsModal
                isOpen={isSettingsModalOpen}
                onClose={() => setIsSettingsModalOpen(false)}
                settings={settings}
                onSettingsChange={handleSettingsChange}
            />
            
            {/* Debug Overlay */}
            <DebugOverlay
                isVisible={settings.showDebugOverlay}
                provider={storageProvider}
                isMinimized={settings.debugOverlayMinimized}
                onMinimizeToggle={handleDebugOverlayMinimizeToggle}
                onDataSeeded={handleDataSeeded}
                onDataCleared={handleDataCleared}
                onProviderChange={handleProviderChange}
            />
        </div>
    );
};

export default App;